/**
 * Default (Polish) translation dictionary.
 *
 * In the previous DOM-driven build these strings lived inline in the hosting
 * page's index.html (as `data-i18n` element contents) and the few direction /
 * message keys came from `i18n/pl.json`. Now the markup lives in this bundle,
 * so the full Polish dictionary is baked in here. At runtime the I18nProvider
 * still fetches `i18n/pl.json` and `i18n/en.json` from the host so deployments
 * can override or extend any string.
 */
export const pl: Record<string, string> = {
    title: "Map",
    copy: "Kopiuj",
    save: "Zapisz",
    "save-png": "Zapisz jako PNG",
    "save-svg": "Zapisz jako SVG",
    search: "Szukaj",
    "find-path": "Wyznacz ścieżkę",
    settings: "Ustawienia",
    help: "Pomoc",
    "room-count": "Ilość lokacji",
    exits: "Wyjścia",
    "search-label": "Numer lokacji lub pełne imię adresata paczki:",
    "start-loc": "Lokacja startowa:",
    "end-loc": "Lokacja docelowa:",
    "location-size": "Wielkość lokacji",
    "line-thickness": "Grubość linii",
    "uniform-areas": "Jednakowa wielkość poziomów strefy",
    "keep-zoom-level": "Zachowuj poziom przybliżenia",
    "room-shape": "Kształt lokacji",
    rectangle: "Prostokąt",
    "rounded-rectangle": "Zaokrąglony prostokąt",
    circle: "Koło",
    "label-render-mode": "Tryb etykiet",
    "label-image": "Obraz",
    "label-data": "Dane",
    "label-data-transparent": "Dane (przezroczyste)",
    "label-none": "Brak",
    "location-borders": "Krawędzie lokacji",
    "render-mode": "Tryb renderowania",
    "render-normal": "Normalny",
    "render-pencil": "Ołówek",
    "render-parchment": "Pergamin",
    "render-parchment-pencil": "Pergamin + ołówek",
    "render-isometric": "Izometryczny",
    "render-isometric-parchment": "Izometryczny + pergamin",
    "render-blueprint": "Blueprint",
    "render-neon": "Neon",
    "render-gradient": "Gradient",
    "iso-rotation": "Obrót izometryczny",
    "room-style": "Styl lokacji",
    "render-frame": "Ramki",
    "render-colored": "Kolorowy",
    "emboss-locations": "Wypukłe lokacje",
    "area-names": "Nazwy stref",
    "area-exit-labels": "Etykiety wyjść ze stref",
    grid: "Siatka",
    background: "Tło mapy",
    "line-color": "Kolor linii",
    minimap: "Minimapa",
    defaults: "Domyślne",
    "this-help": "ta pomoc",
    "search-help": "szukaj",
    "zoom-in": "przybliżenie",
    "zoom-out": "oddalenie",
    "map-move": "przesuwanie mapy",
    "move-instruction":
        "Dodatkowo z zaznaczonej lokacji można po zwykłych wyjściach się poruszać za pomocą klawiatury numerycznej",
    author: "Autor",
    "switch-map-version": "Przełącz wersje mapy:",
    "location-id": "ID Lokacji",
    name: "Nazwa",
    coordinates: "Koordynaty",
    special: "Specjalne",
    other: "Inne",
    packages: "Paczki",
    paths: "Ścieżki",
    // directions
    north: "północ",
    south: "południe",
    east: "wschód",
    west: "zachód",
    northeast: "północny-wschód",
    northwest: "północny-zachód",
    southeast: "południowy-wschód",
    southwest: "południowy-zachód",
    up: "góra",
    down: "dół",
    // messages
    copied: "Skopiowana do schowka",
    "no-clipboard": "Twoja przeglądarka nie wspiera kopiowania do schowka",
    "settings-saved": "Ustawienia zapisane",
    "location-not-found": "Nie znaleziono takiej lokacji",
    delete: "Usuń",
    "no-path": "Nie udało się znaleźć ścieżki",
    mapLoadError: "Nie udało się wczytać danych mapy.",
};

/** Baked English dictionary. Brand-neutral (no Arkadia title/remark). */
export const en: Record<string, string> = {
    title: "Map",
    copy: "Copy",
    save: "Save",
    "save-png": "Save as PNG",
    "save-svg": "Save as SVG",
    search: "Search",
    "find-path": "Find path",
    settings: "Settings",
    help: "Help",
    "room-count": "Room count",
    exits: "Exits",
    "search-label": "Location number or full name of package recipient:",
    "start-loc": "Start location:",
    "end-loc": "Destination:",
    "location-size": "Location size",
    "line-thickness": "Line thickness",
    "uniform-areas": "Uniform area level size",
    "keep-zoom-level": "Keep zoom level",
    "room-shape": "Location shape",
    rectangle: "Rectangle",
    "rounded-rectangle": "Rounded rectangle",
    circle: "Circle",
    "label-render-mode": "Label mode",
    "label-image": "Image",
    "label-data": "Data",
    "label-data-transparent": "Data (transparent)",
    "label-none": "None",
    "location-borders": "Location borders",
    "render-mode": "Render mode",
    "render-normal": "Normal",
    "render-pencil": "Pencil",
    "render-parchment": "Parchment",
    "render-parchment-pencil": "Parchment + pencil",
    "render-isometric": "Isometric",
    "render-isometric-parchment": "Isometric + parchment",
    "render-blueprint": "Blueprint",
    "render-neon": "Neon",
    "render-gradient": "Gradient",
    "iso-rotation": "Isometric rotation",
    "room-style": "Location style",
    "render-frame": "Frame",
    "render-colored": "Colored",
    "emboss-locations": "Emboss locations",
    "area-names": "Area names",
    "area-exit-labels": "Area exit labels",
    grid: "Grid",
    background: "Background",
    "line-color": "Line color",
    minimap: "Minimap",
    defaults: "Defaults",
    "this-help": "this help",
    "search-help": "search",
    "zoom-in": "zoom in",
    "zoom-out": "zoom out",
    "map-move": "move around map",
    "move-instruction":
        "Additionally from selected location you can move around passing through regular exits by using num keyboard",
    author: "Author",
    "switch-map-version": "Switch map version:",
    "location-id": "Location ID",
    name: "Name",
    coordinates: "Coordinates",
    special: "Special",
    other: "Other",
    packages: "Packages",
    paths: "Paths",
    // directions
    north: "north",
    south: "south",
    east: "east",
    west: "west",
    northeast: "northeast",
    northwest: "northwest",
    southeast: "southeast",
    southwest: "southwest",
    up: "up",
    down: "down",
    // messages
    copied: "Copied to clipboard",
    "no-clipboard": "Your browser does not support clipboard",
    "settings-saved": "Settings saved",
    "location-not-found": "Location not found",
    delete: "Delete",
    "no-path": "Could not find path",
    mapLoadError: "Failed to load map data.",
};

export type Dictionary = Record<string, string>;

/** Translations that ship with the bundle, keyed by language code. */
export const bakedDictionaries: Record<string, Dictionary> = {pl, en};
